import React, { useRef } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// context
import { useSearchBar } from "core/SearchBar/context/useSearchBar.js";

// styles
import { getBorderStyles } from "../index";
import { regular } from "core/fontStyles";
import colors from "core/colors";
import breakpoints from "core/breakpoints";
import { convertPxToRem } from "frontend/components/helpers";

export const InputStyled = styled.input`
    flex: 1;
    width: 100%;
    background-color: ${colors.primary.snow};
    height: 100%;
    padding: ${convertPxToRem(10)};
    ${regular}
    font-size: ${convertPxToRem(16)};

    display: flex;
    align-items: center;
    justify-content: center;

    &::placeholder {
        color: ${colors.secondary.bushrock};
    }

    border-radius: 0px;
    border: none;
    border-top: ${({ $borderStyles }) => $borderStyles.border};
    border-bottom: ${({ $borderStyles }) => $borderStyles.border};

    &:focus {
        outline: none;
        border-color: ${({ $borderStyles }) => $borderStyles.focusColor};
    }

    &:hover {
        border-color: ${({ $borderStyles }) => $borderStyles.hoverColor};
    }

    /* overriding Legacy Page Styles */
    &[type=search] {
        box-sizing: border-box;

        &::-webkit-search-cancel-button {
            transform: scale(${({ $scale }) => $scale});
            cursor: pointer;
        }

        /* Hide IOS injected Magnifying Glass */
        &::-webkit-search-decoration {
            display: none;
        }
    }

    &[data-responsive-placeholder]::placeholder {
        color: transparent;
    }
`;

/* If the placeholder text is dynamic, which text changes based on screen size, then
 * we use `content` to swap placeholder text via CSS (fixes flicker after hydration).
 * But since input::placeholder does not support `content`, we're wrapping the input
 * in a relative positioned container, and then adding a span overlay that contains
 * the dynamic placeholder text.
 *
 * The overlay has no pointer-events to let clicks pass through to the input. The input's
 * placeholder text is transparent, but still accessible to screen-readers. The overlay
 * has `display:none` once the input has a value and hiding the native placeholder.
 */
const InputWrapperStyled = styled.div`
    position: relative;
    display: flex;
`;
export const ResponsivePlaceholderStyled = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    padding: ${convertPxToRem(10)};
    pointer-events: none;
    overflow: hidden;
    ${regular}
    font-size: ${convertPxToRem(16)};
    color: ${colors.secondary.bushrock};

    &::before {
        content: attr(data-placeholder-mobile);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    @media (min-width: ${breakpoints.tablet.rem}) {
        &::before {
            content: attr(data-placeholder-tablet);
        }
    }

    @media (min-width: ${breakpoints.desktop.rem}) {
        &::before {
            content: attr(data-placeholder-desktop);
        }
    }

    ${InputStyled}:not(:placeholder-shown) + & {
        display: none;
    }
`;

// component
export const Input = ({ className }) => {
    const {
        isDarkMode,
        inputValue,
        setInputValue,
        clearButtonScale,
        isDisabled,
        inputOnFocus,
        inputOnBlur,
        searchInputRef,
        dropDown: {
            selectedOption: { placeholder }
        }
    } = useSearchBar();

    // Refs
    const defaultInputRef = useRef();
    const inputRef = searchInputRef || defaultInputRef;
    const isResponsive = typeof placeholder === "object";

    const input = (
        <InputStyled
            ref={inputRef}
            className={isResponsive ? undefined : className}
            aria-label={"Search term"}
            name={"q"}
            type={"search"}
            $borderStyles={getBorderStyles(isDarkMode)}
            $scale={clearButtonScale}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onFocus={inputOnFocus}
            onBlur={inputOnBlur}
            disabled={isDisabled}
            placeholder={isResponsive ? placeholder.mobile : placeholder}
            data-responsive-placeholder={isResponsive ? "" : undefined}
        />
    );

    if (!isResponsive) {
        return input;
    }

    return (
        <InputWrapperStyled className={className}>
            {input}
            <ResponsivePlaceholderStyled
                aria-hidden="true"
                data-placeholder-mobile={placeholder.mobile}
                data-placeholder-tablet={placeholder.tablet}
                data-placeholder-desktop={placeholder.desktop}
            />
        </InputWrapperStyled>
    );
};

export default Input;

// prop-types
Input.propTypes = {
    className: PropTypes.string
};
